import { useMemo, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { formatAUD, getFinancialYear, getFinancialYearStart, getFinancialYearEnd } from '@/lib/format';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Separator } from '@/components/ui/separator';
import { Switch } from '@/components/ui/switch';
import { Heart, AlertTriangle, ShieldCheck, DollarSign, TrendingUp } from 'lucide-react';
import { cn } from '@/lib/utils';

// FY 2024-25 thresholds (ATO)
// MLS income thresholds for singles
const MLS_TIERS = [
  { label: 'No MLS', min: 0, max: 93000, rate: 0 },
  { label: 'Tier 1', min: 93001, max: 108000, rate: 1.0 },
  { label: 'Tier 2', min: 108001, max: 144000, rate: 1.25 },
  { label: 'Tier 3', min: 144001, max: Infinity, rate: 1.5 },
];

// PHI rebate tiers (singles) - age < 65
const PHI_REBATE_TIERS = [
  { label: 'Base Tier', min: 0, max: 93000, rebateUnder65: 24.608, rebate65to69: 28.710, rebate70plus: 32.812 },
  { label: 'Tier 1', min: 93001, max: 108000, rebateUnder65: 16.405, rebate65to69: 20.507, rebate70plus: 24.608 },
  { label: 'Tier 2', min: 108001, max: 144000, rebateUnder65: 8.202, rebate65to69: 12.303, rebate70plus: 16.405 },
  { label: 'Tier 3', min: 144001, max: Infinity, rebateUnder65: 0, rebate65to69: 0, rebate70plus: 0 },
];

const MEDICARE_LEVY_RATE = 0.02; // 2%
const MEDICARE_LOW_INCOME_THRESHOLD = 26000; // singles
const MEDICARE_SHADE_IN_THRESHOLD = 32500;

const HEALTH_CATEGORIES = [
  'Medicare', 'Health', 'Health Insurance', 'Dental', 'Optical',
  'Pharmacy', 'Physiotherapy', 'Psychology', 'Hospital', 'Ambulance',
];

function getFYOptions() {
  const now = new Date();
  const startYear = now.getMonth() >= 6 ? now.getFullYear() : now.getFullYear() - 1;
  const options: { label: string; startYear: number }[] = [];
  for (let i = 0; i < 5; i++) {
    const y = startYear - i;
    options.push({ label: `${y}-${(y + 1).toString().slice(-2)}`, startYear: y });
  }
  return options;
}

export default function MedicareHealthPage() {
  const { data } = useAuth();
  const fyOptions = useMemo(() => getFYOptions(), []);
  const [selectedFY, setSelectedFY] = useState(fyOptions[0].label);
  const [hasPHI, setHasPHI] = useState(false);
  const [annualPremium, setAnnualPremium] = useState('');
  const [ageGroup, setAgeGroup] = useState<'under65' | '65to69' | '70plus'>('under65');
  const [isFamily, setIsFamily] = useState(false);

  const selectedStartYear = fyOptions.find(o => o.label === selectedFY)!.startYear;
  const fyStart = new Date(selectedStartYear, 6, 1);
  const fyEnd = new Date(selectedStartYear + 1, 5, 30);

  const stats = useMemo(() => {
    const txns = data.transactions.filter(t => {
      const d = new Date(t.date);
      return d >= fyStart && d <= fyEnd;
    });

    const totalIncome = txns.filter(t => t.type === 'income').reduce((s, t) => s + t.amount, 0);

    const healthExpenses = txns.filter(t =>
      t.type === 'expense' && HEALTH_CATEGORIES.some(hc =>
        t.category.toLowerCase().includes(hc.toLowerCase())
      )
    );
    const totalHealthSpend = healthExpenses.reduce((s, t) => s + t.amount, 0);

    const healthByCategory: Record<string, number> = {};
    for (const t of healthExpenses) {
      healthByCategory[t.category] = (healthByCategory[t.category] || 0) + t.amount;
    }

    return { totalIncome, totalHealthSpend, healthByCategory, healthExpenses };
  }, [data.transactions, fyStart, fyEnd]);

  // MLS calculation
  const mlsThresholdMultiplier = isFamily ? 2 : 1;
  const mlsTier = MLS_TIERS.find(t =>
    stats.totalIncome >= t.min * mlsThresholdMultiplier &&
    stats.totalIncome <= (t.max === Infinity ? Infinity : t.max * mlsThresholdMultiplier)
  ) || MLS_TIERS[0];
  const mlsAmount = hasPHI ? 0 : (mlsTier.rate / 100) * stats.totalIncome;
  const mlsApplies = !hasPHI && mlsTier.rate > 0;

  // Medicare Levy calculation
  let medicareLevy = 0;
  const lowThreshold = MEDICARE_LOW_INCOME_THRESHOLD * (isFamily ? 2 : 1);
  const shadeThreshold = MEDICARE_SHADE_IN_THRESHOLD * (isFamily ? 2 : 1);
  if (stats.totalIncome <= lowThreshold) {
    medicareLevy = 0;
  } else if (stats.totalIncome <= shadeThreshold) {
    medicareLevy = (stats.totalIncome - lowThreshold) * 0.10;
  } else {
    medicareLevy = stats.totalIncome * MEDICARE_LEVY_RATE;
  }

  // PHI Rebate
  const rebateTier = PHI_REBATE_TIERS.find(t =>
    stats.totalIncome >= t.min * mlsThresholdMultiplier &&
    stats.totalIncome <= (t.max === Infinity ? Infinity : t.max * mlsThresholdMultiplier)
  ) || PHI_REBATE_TIERS[0];
  const rebateRate = ageGroup === 'under65' ? rebateTier.rebateUnder65
    : ageGroup === '65to69' ? rebateTier.rebate65to69
    : rebateTier.rebate70plus;
  const premiumNum = parseFloat(annualPremium) || 0;
  const rebateAmount = (rebateRate / 100) * premiumNum;

  return (
    <div className="px-4 pt-6 pb-24">
      <div className="mb-4 flex items-center justify-between">
        <div>
          <h1 className="font-display text-2xl font-bold text-foreground flex items-center gap-2">
            <Heart className="h-6 w-6 text-destructive" />
            Medicare & Health
          </h1>
          <p className="text-sm text-muted-foreground">Levy, surcharge & rebate calculator</p>
        </div>
        <Select value={selectedFY} onValueChange={setSelectedFY}>
          <SelectTrigger className="w-[120px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {fyOptions.map(o => (
              <SelectItem key={o.label} value={o.label}>FY {o.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Your details */}
      <Card className="mb-4">
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Your Details</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <Label htmlFor="family-toggle">Family (doubles thresholds)</Label>
            <Switch id="family-toggle" checked={isFamily} onCheckedChange={setIsFamily} />
          </div>
          <div className="flex items-center justify-between">
            <Label htmlFor="phi-toggle">Have Private Health Insurance?</Label>
            <Switch id="phi-toggle" checked={hasPHI} onCheckedChange={setHasPHI} />
          </div>
          {hasPHI && (
            <>
              <div>
                <Label htmlFor="premium">Annual Premium ($)</Label>
                <Input id="premium" type="number" placeholder="e.g. 2400" value={annualPremium}
                  onChange={e => setAnnualPremium(e.target.value)} className="mt-1" />
              </div>
              <div>
                <Label>Age Group</Label>
                <Select value={ageGroup} onValueChange={v => setAgeGroup(v as any)}>
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="under65">Under 65</SelectItem>
                    <SelectItem value="65to69">65–69</SelectItem>
                    <SelectItem value="70plus">70+</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Income summary */}
      <Card className="mb-4">
        <CardContent className="flex items-center gap-4 p-4">
          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-success/10">
            <DollarSign className="h-5 w-5 text-success" />
          </div>
          <div>
            <p className="text-sm text-muted-foreground">FY {selectedFY} Income</p>
            <p className="font-display text-xl font-bold">{formatAUD(stats.totalIncome)}</p>
          </div>
        </CardContent>
      </Card>

      {/* Medicare Levy */}
      <Card className="mb-4">
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <ShieldCheck className="h-4 w-4 text-primary" />
            Medicare Levy (2%)
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between text-sm">
            <span className="text-muted-foreground">Estimated levy</span>
            <span className="font-display font-bold">{formatAUD(medicareLevy)}</span>
          </div>
          {stats.totalIncome <= lowThreshold && (
            <p className="mt-2 text-xs text-success">Below low-income threshold — no levy payable</p>
          )}
          {stats.totalIncome > lowThreshold && stats.totalIncome <= shadeThreshold && (
            <p className="mt-2 text-xs text-warning-foreground">Shade-in range — reduced levy applies</p>
          )}
        </CardContent>
      </Card>

      {/* MLS */}
      <Card className={cn('mb-4', mlsApplies && 'border-warning')}>
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            {mlsApplies ? <AlertTriangle className="h-4 w-4 text-warning" /> : <ShieldCheck className="h-4 w-4 text-success" />}
            Medicare Levy Surcharge
          </CardTitle>
        </CardHeader>
        <CardContent>
          {hasPHI ? (
            <div className="space-y-1">
              <p className="text-sm text-success font-medium">No MLS — you have private health cover ✓</p>
              <p className="text-xs text-muted-foreground">
                Having PHI exempts you from the Medicare Levy Surcharge
              </p>
            </div>
          ) : mlsTier.rate === 0 ? (
            <div className="space-y-1">
              <p className="text-sm text-success font-medium">No MLS — income below threshold</p>
              <p className="text-xs text-muted-foreground">
                MLS applies to {isFamily ? 'families' : 'singles'} earning over {formatAUD((isFamily ? 2 : 1) * 93000)}
              </p>
            </div>
          ) : (
            <div className="space-y-2">
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">{mlsTier.label} ({mlsTier.rate}%)</span>
                <span className="font-display font-bold text-warning">{formatAUD(mlsAmount)}</span>
              </div>
              <p className="text-xs text-muted-foreground">
                Consider getting PHI to avoid this surcharge. It may cost less than the MLS.
              </p>
              {premiumNum === 0 && (
                <p className="text-xs text-primary font-medium">
                  💡 PHI starting from ~{formatAUD(mlsAmount * 0.7)}/yr could save you money
                </p>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* PHI Rebate */}
      {hasPHI && (
        <Card className="mb-4">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <TrendingUp className="h-4 w-4 text-primary" />
              PHI Rebate
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">Rebate tier</span>
              <Badge variant="secondary">{rebateTier.label} — {rebateRate.toFixed(1)}%</Badge>
            </div>
            {premiumNum > 0 && (
              <>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">Annual premium</span>
                  <span className="font-semibold">{formatAUD(premiumNum)}</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">Estimated rebate</span>
                  <span className="font-display font-bold text-success">{formatAUD(rebateAmount)}</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">Effective cost after rebate</span>
                  <span className="font-semibold">{formatAUD(premiumNum - rebateAmount)}</span>
                </div>
              </>
            )}
            {premiumNum === 0 && (
              <p className="text-xs text-muted-foreground">Enter your annual premium above to see your rebate estimate</p>
            )}
          </CardContent>
        </Card>
      )}

      {/* Health spending breakdown */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Health Spending</CardTitle>
          <p className="text-sm text-muted-foreground">
            {stats.healthExpenses.length} health transactions · {formatAUD(stats.totalHealthSpend)} total
          </p>
        </CardHeader>
        <CardContent className="space-y-2">
          {Object.keys(stats.healthByCategory).length === 0 ? (
            <p className="text-sm text-muted-foreground py-4 text-center">
              No health-related expenses found. Transactions categorised as {HEALTH_CATEGORIES.slice(0, 4).join(', ')} etc. will appear here.
            </p>
          ) : (
            Object.entries(stats.healthByCategory)
              .sort((a, b) => b[1] - a[1])
              .map(([cat, total]) => (
                <div key={cat} className="flex items-center justify-between rounded-lg bg-muted/50 px-3 py-2">
                  <p className="text-sm font-medium">{cat}</p>
                  <Badge variant="secondary" className="font-display font-semibold">{formatAUD(total)}</Badge>
                </div>
              ))
          )}
        </CardContent>
      </Card>

      <p className="mt-4 text-xs text-muted-foreground text-center">
        Thresholds based on FY 2024-25 ATO rates. Consult a tax professional for personalised advice.
      </p>
    </div>
  );
}
